'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import { List, Map as ImmutableMap, OrderedMap, Seq } from 'immutable';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import I18n from 'I18n';
import classNames from 'classnames';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import UILink from 'UIComponents/link/UILink';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import Branding from 'MeetingsBase/models/Branding';
import { getEvent } from 'MeetingsLib/selectors/BookInfoSelectors';
import { shadeColor } from 'MeetingsLib/utils/color';
import { getTextColor } from 'MeetingsBase/utils/color';
import { isDateInPast } from 'MeetingsBase/utils/dates';
import { UNAVAILABLE_REASONS } from 'MeetingsLib/constants/enums';

class DatePickerButton extends Component {
  constructor(...args) {
    super(...args);

    this.isValid = () => {
      const {
        availableDates,
        date,
        isLoadingLinkAvailabilityPage
      } = this.props;

      if (isLoadingLinkAvailabilityPage) {
        return false;
      }

      return availableDates.includes(date);
    };

    this.getClassNames = () => {
      const {
        selected
      } = this.props;
      const valid = this.isValid();
      return classNames('date-picker-btn', valid ? "valid" : "disabled", selected && "selected");
    };

    this.shouldTroubleshoot = () => {
      const {
        date,
        availableDates,
        isTroubleshooting
      } = this.props;

      if (!isTroubleshooting) {
        return false;
      }

      const dayHasAvailability = availableDates.includes(date);
      return !dayHasAvailability;
    };

    this.getUnbookableReason = () => {
      const {
        bookableDates,
        configuredAvailabilityByDate,
        currentTime,
        date,
        troubleshootTimesForSelectedDurationByDay,
        minimumNoticeTimeBusyDays,
        userTimezone
      } = this.props;

      if (!this.shouldTroubleshoot()) {
        return null;
      }

      if (isDateInPast(date, currentTime, userTimezone)) {
        return UNAVAILABLE_REASONS.PAST;
      }

      if (!bookableDates.includes(date)) {
        return UNAVAILABLE_REASONS.DATE_NOT_CONFIGURED;
      }

      if (!configuredAvailabilityByDate.get(date)) {
        return UNAVAILABLE_REASONS.TIME_NOT_CONFIGURED;
      }

      if (minimumNoticeTimeBusyDays.includes(date)) {
        return UNAVAILABLE_REASONS.MINIMUM_NOTICE;
      }

      const troubleshootTimesForDate = troubleshootTimesForSelectedDurationByDay.get(date);
      const overlapsWithDayLongEvent = troubleshootTimesForDate && troubleshootTimesForDate.find(troubleshotTime => troubleshotTime.reason === UNAVAILABLE_REASONS.ALL_DAY_EVENT);

      if (overlapsWithDayLongEvent) {
        return UNAVAILABLE_REASONS.ALL_DAY_EVENT;
      }

      return null;
    };

    this.getStyle = () => {
      const {
        selected,
        branding: {
          primaryColor
        }
      } = this.props;
      const primaryTextColor = getTextColor(primaryColor);

      if (selected) {
        return {
          color: primaryColor,
          background: primaryTextColor
        };
      }

      if (this.shouldTroubleshoot() && this.getUnbookableReason()) {
        const shadedColor = shadeColor(primaryColor, 40);
        return {
          color: getTextColor(shadedColor),
          background: shadedColor
        };
      }

      return {
        color: primaryTextColor
      };
    };

    this.getTooltipText = () => {
      const {
        editLinkURL
      } = this.props;
      const unbookableReason = this.getUnbookableReason();

      switch (unbookableReason) {
        case UNAVAILABLE_REASONS.PAST:
          return /*#__PURE__*/_jsx(FormattedMessage, {
            message: "troubleshoot.day.past"
          });

        case UNAVAILABLE_REASONS.DATE_NOT_CONFIGURED:
          return /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "troubleshoot.day.customRange_jsx",
            options: {
              href: editLinkURL,
              external: true
            },
            elements: {
              Link: UILink
            }
          });

        case UNAVAILABLE_REASONS.TIME_NOT_CONFIGURED:
          return /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "troubleshoot.day.unset_jsx",
            options: {
              href: editLinkURL,
              external: true
            },
            elements: {
              Link: UILink
            }
          });

        case UNAVAILABLE_REASONS.MINIMUM_NOTICE:
          return /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "troubleshoot.day.minimum_jsx",
            options: {
              href: editLinkURL,
              external: true
            },
            elements: {
              Link: UILink
            }
          });

        case UNAVAILABLE_REASONS.ALL_DAY_EVENT:
          return /*#__PURE__*/_jsx(FormattedMessage, {
            message: "troubleshoot.day.dayLong"
          });

        default:
          return null;
      }
    };
  }

  render() {
    const {
      date,
      daysToAdd,
      handleSetEventDate,
      selected
    } = this.props;
    const placement = daysToAdd > 3 ? 'top left' : 'top right';
    const isValid = this.isValid();
    const tabIndex = isValid ? 0 : -1;
    const disabled = !isValid;

    const button = /*#__PURE__*/_jsx("td", {
      "data-selenium-test-disabled": disabled,
      children: /*#__PURE__*/_jsx("button", {
        "data-test-id": isValid ? 'available-date' : 'unavailable-date',
        "aria-controls": "available-times-region",
        "aria-pressed": selected,
        "aria-disabled": disabled,
        "aria-label": I18n.moment(date).format('MMMM Do'),
        tabIndex: tabIndex,
        id: selected ? 'selected-date' : '',
        style: this.getStyle(),
        onClick: () => handleSetEventDate(date),
        onKeyPress: e => e.key === 'Enter' && handleSetEventDate(date),
        className: this.getClassNames(),
        children: /*#__PURE__*/_jsx("span", {
          children: I18n.moment(date).format('D')
        })
      })
    });

    if (!this.props.isTroubleshooting) {
      return button;
    }

    const tooltipText = this.getTooltipText();
    return /*#__PURE__*/_jsx(UITooltip, {
      disabled: !tooltipText,
      maxWidth: 210,
      placement: placement,
      title: tooltipText,
      use: "longform",
      children: button
    });
  }

}

DatePickerButton.propTypes = {
  availableDates: PropTypes.instanceOf(Seq).isRequired,
  branding: PropTypes.instanceOf(Branding).isRequired,
  currentTime: PropTypes.number.isRequired,
  date: PropTypes.string.isRequired,
  daysToAdd: PropTypes.number.isRequired,
  handleSetEventDate: PropTypes.func.isRequired,
  selected: PropTypes.bool,
  // troubleshoot mode
  bookableDates: PropTypes.instanceOf(List),
  configuredAvailabilityByDate: PropTypes.instanceOf(OrderedMap),
  editLinkURL: PropTypes.string.isRequired,
  troubleshootTimesForSelectedDurationByDay: PropTypes.instanceOf(ImmutableMap),
  minimumNoticeTimeBusyDays: PropTypes.instanceOf(List),
  isTroubleshooting: PropTypes.bool,
  userTimezone: PropTypes.string.isRequired,
  isLoadingLinkAvailabilityPage: PropTypes.bool
};
export default connect((state, props) => ({
  selected: getEvent(state).dateString === props.date
}))(DatePickerButton);